import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, X, Image as ImageIcon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { FeedItem, postsApi, mediaApi } from '@/lib/api'
import { toast } from '@/components/ui/use-toast'

interface EditPostModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: FeedItem
  onEdit?: (postId: string, content: string, mediaIds: string[]) => void
}

export function EditPostModal({
  open,
  onOpenChange,
  item,
  onEdit,
}: EditPostModalProps) {
  const queryClient = useQueryClient()
  const post = item.post.post
  const media = item.post.media || []

  const [content, setContent] = useState(post.content || '')
  const [existingMedia, setExistingMedia] = useState(media)
  const [newMediaFiles, setNewMediaFiles] = useState<File[]>([])
  const [newMediaPreviews, setNewMediaPreviews] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setContent(post.content || '')
      setExistingMedia(media)
      setNewMediaFiles([])
      setNewMediaPreviews([])
    }
  }, [open, post.content, media])

  const updateMutation = useMutation({
    mutationFn: async ({
      postId,
      content,
      mediaIds,
    }: {
      postId: string
      content: string
      mediaIds: string[]
    }) => {
      return postsApi.updatePost(postId, {
        content,
        media_ids: mediaIds,
        post_type: post.post_type as 'text' | 'photo' | 'album' | 'link' | 'ai_photo' | 'music_visual',
        visibility: post.visibility as 'public' | 'friends' | 'private' | 'group',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      queryClient.invalidateQueries({ queryKey: ['posts'] })

      toast({
        title: 'Post updated',
        description: 'Your post has been updated successfully',
      })

      if (onEdit) {
        const allMediaIds = [
          ...existingMedia.map((m) => m.id),
          // New media IDs would be added here after upload
        ]
        onEdit(post.id, content, allMediaIds)
      }

      onOpenChange(false)
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update post',
        description: error.message || 'An error occurred while updating the post',
        variant: 'destructive',
      })
    },
    onSettled: () => {
      setIsSubmitting(false)
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Create preview URLs
    const previews = files.map((file) => URL.createObjectURL(file))
    setNewMediaFiles((prev) => [...prev, ...files])
    setNewMediaPreviews((prev) => [...prev, ...previews])
  }

  const handleRemoveExistingMedia = (mediaId: string) => {
    setExistingMedia((prev) => prev.filter((m) => m.id !== mediaId))
  }

  const handleRemoveNewMedia = (index: number) => {
    setNewMediaFiles((prev) => prev.filter((_, i) => i !== index))
    setNewMediaPreviews((prev) => {
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index])
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleSubmit = async () => {
    if (!content.trim() && existingMedia.length === 0 && newMediaFiles.length === 0) {
      toast({
        title: 'Content required',
        description: 'Post must have text or media',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)

    try {
      let uploadedMediaIds: string[] = []

      // Upload new media files if any
      if (newMediaFiles.length > 0) {
        const uploadResponse = await mediaApi.uploadPhotos(newMediaFiles)
        const uploadedMedia = Array.isArray(uploadResponse.data)
          ? uploadResponse.data
          : [uploadResponse.data]
        uploadedMediaIds = uploadedMedia.map((m) => m.id)
      }

      // Combine existing media IDs with newly uploaded media IDs
      const allMediaIds = [
        ...existingMedia.map((m) => m.id),
        ...uploadedMediaIds,
      ]

      updateMutation.mutate({
        postId: post.id,
        content: content.trim(),
        mediaIds: allMediaIds,
      })
    } catch (error) {
      setIsSubmitting(false)
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload media',
        variant: 'destructive',
      })
    }
  }

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      newMediaPreviews.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [newMediaPreviews])

  const totalMedia = existingMedia.length + newMediaFiles.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit post</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Text content */}
          <Textarea
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-[120px] resize-none"
            disabled={isSubmitting}
          />

          {/* Media preview */}
          {totalMedia > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {/* Existing media */}
              {existingMedia.map((m) => (
                <div key={m.id} className="relative aspect-square">
                  <img
                    src={m.url}
                    alt=""
                    className="h-full w-full rounded-lg object-cover"
                  />
                  <button
                    onClick={() => handleRemoveExistingMedia(m.id)}
                    className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                    disabled={isSubmitting}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {/* New media previews */}
              {newMediaPreviews.map((preview, index) => (
                <div key={preview} className="relative aspect-square">
                  <img
                    src={preview}
                    alt=""
                    className="h-full w-full rounded-lg object-cover"
                  />
                  <button
                    onClick={() => handleRemoveNewMedia(index)}
                    className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                    disabled={isSubmitting}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add media button */}
          <div>
            <input
              type="file"
              id="edit-post-media"
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isSubmitting}
            />
            <label htmlFor="edit-post-media">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="cursor-pointer"
                disabled={isSubmitting}
                asChild
              >
                <span>
                  <ImageIcon className="mr-2 h-4 w-4" />
                  Add photos/videos
                </span>
              </Button>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
